/**
 * How the building blocks look: hull (timber, iron plate, glass, brick,
 * stone), back walls, ladders, laid ground (paving, decking, deck plate),
 * braces, lattices, railings and gratings. Each comes in the ship parts'
 * three families (ironclad, brassworks, shipwright; see shipArt.ts), since a
 * hull should match the machinery bolted to it, and each family's is a block
 * of its own (IRONCLAD_PLANK ...). TileAtlas paints every one from here
 * (BUILD_PAINT).
 */
import { BRASS, GLASS, IRON, ROPE, S, WALL, WOOD, beam, disc, fill, glassPane, grain, hash, line, nail, noise, panel, path, pixels, plate, rivet, stoneField, tone, type Ctx } from "./paint";
import type { Family } from "./shipArt";

type Ramp = readonly string[];

export interface BlockDesign {
  family: Family;
  blurb: string;
  paint: (ctx: Ctx, v: number) => void;
}

export interface BlockGroup {
  id: string;
  title: string;
  note: string;
  /** The blocks it paints, by their name in the registry without the family (IRONCLAD_PLANK is "PLANK"). */
  key: string;
  designs: BlockDesign[];
}

const DARK = "#101214";

// ----- building: the families' materials

/** Tarred timber: dark brown, the grain still showing. */
const TAR = ["#1c1510", "#2e241a", "#413325", "#554332", "#6a5640"] as const;
/** Darker, bluer iron for armour. */
const ARMOUR = ["#1a1d21", "#2b3036", "#3f454c", "#555c64", "#6f777f"] as const;
/** Varnished mahogany. */
const MAHOGANY = ["#2a1208", "#4a1e0e", "#6b2f16", "#8a4222", "#a85a30"] as const;
/** Brick: fired dark, red, and pale yellow. */
const BRICK_DARK = ["#1e1512", "#33231c", "#4a3128", "#5c3e32", "#705040"] as const;
const BRICK_RED = ["#4a1e14", "#7a3222", "#a0472e", "#b85a3a", "#cf7a52"] as const;
const BRICK_PALE = ["#5e5234", "#8a7a4e", "#aa9a68", "#c4b680", "#dcd0a0"] as const;
const MORTAR = ["#2a2622", "#6a655c", "#9a9488"] as const;
/** Cut stone: dark basalt, dressed sandstone, grey fieldstone. */
const BASALT = ["#1c1c1e", "#2c2d30", "#3c3e42", "#4c4f54", "#5e6268"] as const;
const SANDSTONE = ["#5a4a34", "#7e6a4a", "#9a845c", "#b49c70", "#ccb688"] as const;
const FIELDSTONE = ["#3e3a34", "#56514a", "#6e685f", "#857e74", "#9c958a"] as const;

/** Boards across the tile, `wide` tall, with a staggered joint in each and nails either side of it. */
function boards(ctx: Ctx, ramp: Ramp, wide: number, seed: number, v: number, nails = true): void {
  pixels(ctx, S, S, (x, y) => {
    const b = Math.floor(y / wide);
    const r = y % wide;
    if (r === wide - 1) return ramp[0]!;
    let k = 0.5 + (hash(b, v, seed) - 0.5) * 0.25 + grain(x, y, seed + v * 31 + b);
    if (r === 0) k += 0.26;
    else if (r === wide - 2) k -= 0.2;
    return tone(ramp, k, x, y, 0.45);
  });
  for (let b = 0; b < S / wide; b++) {
    const joint = 4 + Math.floor(hash(b, v, seed + 7) * (S - 8));
    fill(ctx, ramp[0]!, joint, b * wide, 1, wide - 1);
    fill(ctx, ramp[3]!, joint + 1, b * wide, 1, wide - 1);
    if (nails && wide >= 6) {
      nail(ctx, joint - 3, b * wide + Math.floor(wide / 2) - 1);
      nail(ctx, joint + 2, b * wide + Math.floor(wide / 2) - 1);
    }
  }
}

/** A flat metal plate, bevelled, faintly brushed. */
function sheet(ctx: Ctx, ramp: Ramp, seed: number, bevel = 2): void {
  pixels(ctx, S, S, (x, y) => {
    for (let b = 0; b < bevel; b++) {
      if (x === S - 1 - b || y === S - 1 - b) return ramp[b === 0 ? 0 : 1]!;
      if (x === b || y === b) return ramp[b === 0 ? 4 : 3]!;
    }
    const k = 0.5 - ((x + y) / (S * 2)) * 0.2 + (hash(x, 0, seed) - 0.5) * 0.12 + (noise(x, y, 8, seed + 1) - 0.5) * 0.12;
    return tone(ramp, k, x, y, 0.5);
  });
}

/** A round pane of glass (a bullseye), the whorl in it catching the light. */
function glassPaneDisc(ctx: Ctx, cx: number, cy: number, r: number): void {
  pixels(ctx, r * 2, r * 2, (x, y) => {
    const dx = x + 0.5 - r;
    const dy = y + 0.5 - r;
    if (dx * dx + dy * dy > r * r) return null;
    const d = dx + dy;
    if ((d > -6 && d < -3) || (d > 0 && d < 1.5)) return GLASS[4];
    return tone(GLASS, 0.75 - (dy / r) * 0.3 - (Math.hypot(dx, dy) / r) * 0.25, x, y);
  }, cx - r, cy - r);
}

/** A brass bolt head: a brass dome with a dark slot. */
function brassBolt(ctx: Ctx, x: number, y: number): void {
  fill(ctx, BRASS[0], x, y, 3, 3);
  fill(ctx, BRASS[3], x, y, 2, 2);
  fill(ctx, BRASS[4], x, y, 1, 1);
}

/** A bricks course pattern: `h` tall courses of `w` wide bricks, bonded by half a brick a course. */
function bricks(ctx: Ctx, ramp: Ramp, mortar: string, w: number, h: number, seed: number, v: number): void {
  pixels(ctx, S, S, (x, y) => {
    const course = Math.floor(y / h);
    const r = y % h;
    if (r === h - 1) return mortar;
    const ox = x + (course % 2) * (w / 2) + v * 5;
    const c = ((ox % w) + w) % w;
    if (c === w - 1) return mortar;
    let k = 0.5 + (hash(Math.floor(ox / w), course, seed) - 0.5) * 0.4 + (noise(x, y, 4, seed + 1) - 0.5) * 0.2;
    if (r === 0) k += 0.22;
    else if (r === h - 2) k -= 0.2;
    if (c === 0) k += 0.1;
    else if (c === w - 2) k -= 0.1;
    return tone(ramp, k, x, y);
  });
}

/** Dressed ashlar: big blocks in two courses with a chiselled margin round each. */
function ashlar(ctx: Ctx, ramp: Ramp, seed: number, v: number): void {
  pixels(ctx, S, S, (x, y) => {
    const course = y < 16 ? 0 : 1;
    const r = y - course * 16;
    if (r === 15) return ramp[0]!;
    const ox = x + course * 16 + v * 7;
    const c = ((ox % 32) + 32) % 32;
    if (c === 31) return ramp[0]!;
    const margin = r < 2 || r > 12 || c < 2 || c > 28;
    let k = 0.5 + (hash(Math.floor(ox / 32), course, seed) - 0.5) * 0.3 + (noise(x, y, 5, seed + 1) - 0.5) * 0.18;
    if (margin) k += r < 2 || c < 2 ? 0.25 : -0.2;
    else if ((x + y) % 3 === 0) k -= 0.06; // chisel marks
    return tone(ramp, k, x, y);
  });
}

/** A diagonal lattice of `w` wide strips every `gap` pixels, both ways. */
function lattice(ctx: Ctx, w: number, gap: number, dark: string, light: string, straight = false): void {
  pixels(ctx, S, S, (x, y) => {
    const a = straight ? x % gap : ((x + y) % gap + gap) % gap;
    const b = straight ? y % gap : ((x - y) % gap + gap) % gap;
    if (a < w || b < w) return a === 0 || b === 0 ? light : a === w - 1 || b === w - 1 ? dark : null;
    return null;
  });
}

/** A strip (rail, baluster, bar) with a light edge and a dark edge: horizontal if `h` is small. */
function bar(ctx: Ctx, x: number, y: number, w: number, h: number, ramp: Ramp): void {
  fill(ctx, ramp[2]!, x, y, w, h);
  if (w >= h) {
    fill(ctx, ramp[4]!, x, y, w, 1);
    fill(ctx, ramp[0]!, x, y + h - 1, w, 1);
  } else {
    fill(ctx, ramp[4]!, x, y, 1, h);
    fill(ctx, ramp[0]!, x + w - 1, y, 1, h);
  }
}

/** A rope strung across between (x0, y) and (x1, y), sagging `sag`. */
function ropeAcross(ctx: Ctx, x0: number, x1: number, y: number, sag: number): void {
  const pts: number[] = [];
  for (let x = x0; x <= x1; x += 2) {
    const t = (x - x0) / (x1 - x0);
    pts.push(x, y + Math.sin(t * Math.PI) * sag);
  }
  path(ctx, ROPE[0], pts, 3);
  path(ctx, ROPE[2], pts, 1.5);
  for (let x = x0; x <= x1; x += 3) {
    const t = (x - x0) / (x1 - x0);
    fill(ctx, ROPE[1], x, Math.round(y + Math.sin(t * Math.PI) * sag) - 1, 1, 2);
  }
}

// ----- building groups

const HULL_TIMBER: BlockDesign[] = [
  { family: "ironclad", blurb: "Narrow planks tarred nearly black, an iron strap across each joint. Warship hull.", paint: (ctx, v) => {
    boards(ctx, TAR, 8, 3, v, false);
    for (const b of [0, 2]) {
      const j = 6 + Math.floor(hash(b, v, 10) * (S - 16));
      panel(ctx, j - 5, b * 8 + 2, 11, 3, ARMOUR[2], ARMOUR[3], ARMOUR[0]);
      fill(ctx, ARMOUR[0], j - 4, b * 8 + 3, 1, 1);
      fill(ctx, ARMOUR[0], j + 4, b * 8 + 3, 1, 1);
    }
  } },
  { family: "brassworks", blurb: "Wide varnished mahogany planks, the highlight running along each, brass nail heads at the joints.", paint: (ctx, v) => {
    boards(ctx, MAHOGANY, 8, 5, v, false);
    for (let b = 0; b < 4; b++) {
      const j = 4 + Math.floor(hash(b, v, 12) * (S - 8));
      brassBolt(ctx, j - 4, b * 8 + 2);
      brassBolt(ctx, j + 2, b * 8 + 2);
      fill(ctx, MAHOGANY[4], 0, b * 8, S, 1);
    }
  } },
  { family: "shipwright", blurb: "Two broad oak planks a tile, pegged with wooden treenails rather than nailed, the grain showing.", paint: (ctx, v) => {
    boards(ctx, WOOD, 16, 7, v, false);
    for (let b = 0; b < 2; b++) {
      const j = 4 + Math.floor(hash(b, v, 14) * (S - 8));
      for (const x of [j - 5, j + 3]) {
        disc(ctx, WOOD[0], x + 1.5, b * 16 + 7.5, 2);
        disc(ctx, WOOD[3], x + 1.5, b * 16 + 7.5, 1.2);
      }
    }
  } },
];

const HULL_IRON: BlockDesign[] = [
  { family: "ironclad", blurb: "Thick armour plate, a double row of rivets down two edges and a weld seam, in the dark bluish iron of the ship parts.", paint: (ctx, v) => {
    sheet(ctx, ARMOUR, 20 + v, 2);
    for (let i = 0; i < 4; i++) {
      rivet(ctx, 3, 3 + i * 8);
      rivet(ctx, 27, 3 + i * 8);
    }
    fill(ctx, ARMOUR[1], 8, 15 + v, 16, 1);
    fill(ctx, ARMOUR[3], 8, 16 + v, 16, 1);
  } },
  { family: "brassworks", blurb: "Polished plate with a brass edging strip along the top and brass bolt heads in the corners.", paint: (ctx, v) => {
    sheet(ctx, IRON, 24 + v, 1);
    fill(ctx, BRASS[1], 0, 0, S, 3);
    fill(ctx, BRASS[3], 0, 0, S, 1);
    fill(ctx, BRASS[4], 2, 1, S - 4, 1);
    for (const [x, y] of [[3, 6], [26, 6], [3, 26], [26, 26]] as const) brassBolt(ctx, x, y);
  } },
  { family: "shipwright", blurb: "An iron plate bolted onto the timber hull, the planks showing in a narrow margin along two sides, big square bolt heads at the corners.", paint: (ctx, v) => {
    boards(ctx, WOOD, 8, 30 + v, v, false);
    pixels(ctx, S - 3, S - 3, (x, y) => {
      if (x === S - 4 || y === S - 4) return IRON[0];
      if (x === 0 || y === 0) return IRON[4];
      return tone(IRON, 0.5 - ((x + y) / 60) * 0.2 + (noise(x, y, 8, 30 + v) - 0.5) * 0.15, x, y, 0.5);
    });
    for (const [x, y] of [[3, 3], [23, 3], [3, 23], [23, 23]] as const) {
      fill(ctx, IRON[0], x, y, 4, 4);
      fill(ctx, IRON[3], x, y, 3, 3);
      fill(ctx, IRON[4], x, y, 1, 1);
    }
  } },
];

const HULL_GLASS: BlockDesign[] = [
  { family: "ironclad", blurb: "Leaded diamond panes in an iron frame: small lozenges of grey-green glass between lead cames.", paint: (ctx) => {
    glassPane(ctx, 0, 0, S, S);
    pixels(ctx, S, S, (x, y) => {
      const a = ((x + y) % 10 + 10) % 10;
      const b = ((x - y) % 10 + 10) % 10;
      return a === 0 || b === 0 ? IRON[1] : a === 1 || b === 1 ? IRON[3] : null;
    });
    for (const [x, y, w, h] of [[0, 0, S, 2], [0, 0, 2, S]] as const) fill(ctx, IRON[3], x, y, w, h);
    fill(ctx, IRON[1], 0, S - 2, S, 2);
    fill(ctx, IRON[1], S - 2, 0, 2, S);
  } },
  { family: "brassworks", blurb: "A plain pane in a brass frame with one round bullseye set in its middle: a saloon window with a porthole's eye.", paint: (ctx) => {
    glassPane(ctx, 0, 0, S, S);
    disc(ctx, BRASS[0], 16, 16, 9.5);
    disc(ctx, BRASS[3], 16, 16, 8.5);
    glassPaneDisc(ctx, 16, 16, 7);
    disc(ctx, GLASS[1], 16, 16, 1.5);
    for (const [x, y, w, h] of [[0, 0, S, 3], [0, 0, 3, S], [0, S - 3, S, 3], [S - 3, 0, 3, S]] as const) panel(ctx, x, y, w, h, BRASS[2], BRASS[4], BRASS[0]);
  } },
  { family: "shipwright", blurb: "A four-pane window with a timber cross mullion in a timber frame: cottage glazing on a ship.", paint: (ctx) => {
    glassPane(ctx, 0, 0, S, S);
    beam(ctx, 0, 0, 3, S);
    beam(ctx, S - 3, 0, 3, S);
    beam(ctx, 0, 0, S, 3);
    beam(ctx, 0, S - 3, S, 3);
    beam(ctx, 14, 0, 3, S);
    beam(ctx, 0, 14, S, 3);
  } },
];

const BACK_WALL: BlockDesign[] = [
  { family: "ironclad", blurb: "Iron bulkhead sheeting in the dark: overlapping sheets with a rivet row along each lap.", paint: (ctx, v) => {
    pixels(ctx, S, S, (x, y) => {
      const lap = ((y + v * 5) % 16 + 16) % 16;
      if (lap === 0) return "#0c0e11";
      if (lap === 1) return "#2a2f35";
      return tone(["#121417", "#1a1e22", "#22272c", "#2b3137", "#353c43"], 0.5 - (lap / 16) * 0.3 + (noise(x, y, 8, 40 + v) - 0.5) * 0.2, x, y, 0.4);
    });
    for (let x = 4; x < S; x += 8) for (const y of [4, 20]) {
      const yy = (y + v * 5) % S;
      fill(ctx, "#0c0e11", x, yy, 2, 2);
      fill(ctx, "#3a4148", x, yy, 1, 1);
    }
  } },
  { family: "brassworks", blurb: "Mahogany wainscot: a raised panel in a dark frame, a brass rail along the top. A saloon, not a hold.", paint: (ctx, v) => {
    pixels(ctx, S, S, (x, y) => tone(["#1c0c06", "#2e140a", "#3e1c0e", "#4a2412", "#5a2e18"], 0.45 + grain(y, x, 44 + v) * 0.8 + (hash(x >> 3, 0, 45 + v) - 0.5) * 0.15, x, y, 0.5));
    panel(ctx, 4, 8, S - 8, S - 12, "#4a2412", "#6b3418", "#1c0c06");
    panel(ctx, 7, 11, S - 14, S - 18, "#3e1c0e", "#2a1008", "#5a2e18");
    fill(ctx, BRASS[1], 0, 3, S, 2);
    fill(ctx, BRASS[3], 0, 3, S, 1);
  } },
  { family: "shipwright", blurb: "The boards as now, pale oak rather than dark, with a horizontal batten nailed across them.", paint: (ctx, v) => {
    const pale = ["#2a1c10", "#3e2c18", "#523a22", "#64482c", "#765636"] as const;
    pixels(ctx, S, S, (x, y) => {
      const c = x & 7;
      if (c === 7) return pale[0];
      let k = 0.5 + (hash(x >> 3, v, 41) - 0.5) * 0.3 + grain(y, x, 43 + (x >> 3)) * 0.8;
      if (c === 0) k += 0.22;
      else if (c === 6) k -= 0.18;
      return tone(pale, k, x, y, 0.5);
    });
    beam(ctx, 0, 13 + v * 3, S, 5, pale);
    for (let x = 3; x < S; x += 8) nail(ctx, x, 15 + v * 3);
  } },
];

const LADDER: BlockDesign[] = [
  { family: "ironclad", blurb: "Round iron rungs between two flat iron rails, bolted to the bulkhead.", paint: (ctx, v) => {
    BACK_WALL[0]!.paint(ctx, v);
    for (const y of [4, 12, 20, 28]) {
      fill(ctx, "#0c0e11", 7, y + 2, 18, 2);
      bar(ctx, 6, y, 20, 3, IRON);
    }
    bar(ctx, 4, 0, 4, S, IRON);
    bar(ctx, 24, 0, 4, S, IRON);
    for (const y of [6, 22]) {
      rivet(ctx, 5, y);
      rivet(ctx, 25, y);
    }
  } },
  { family: "brassworks", blurb: "Brass rails with turned mahogany rungs, over the wainscot.", paint: (ctx, v) => {
    BACK_WALL[1]!.paint(ctx, v);
    for (const y of [5, 21]) {
      fill(ctx, "#1c0c06", 7, y + 4, 19, 2);
      bar(ctx, 6, y, 20, 4, MAHOGANY);
      disc(ctx, MAHOGANY[3], 9, y + 2, 2.2);
      disc(ctx, MAHOGANY[3], 23, y + 2, 2.2);
    }
    bar(ctx, 4, 0, 4, S, BRASS);
    bar(ctx, 24, 0, 4, S, BRASS);
  } },
  { family: "shipwright", blurb: "Timber rails and rungs, each rung lashed to the rails with rope.", paint: (ctx, v) => {
    BACK_WALL[2]!.paint(ctx, v);
    for (const y of [5, 21]) {
      fill(ctx, WALL[0], 7, y + 4, 19, 2);
      beam(ctx, 6, y, 20, 4);
    }
    beam(ctx, 5, 0, 5, S);
    beam(ctx, 22, 0, 5, S);
    for (const y of [4, 20]) for (const x of [4, 21]) {
      pixels(ctx, 7, 6, (px, py) => ((px + py) % 3 === 0 ? ROPE[0] : (px + py) % 3 === 1 ? ROPE[2] : ROPE[1]), x, y);
    }
  } },
];

const PAVING: BlockDesign[] = [
  { family: "ironclad", blurb: "Tight-fitted dark basalt setts under a worn pale top band: a parade ground.", paint: (ctx, v) => {
    pixels(ctx, S, S, (x, y) => {
      if (y < 4) return tone(["#5a5e64", "#787c83", "#8e939a", "#a2a6ad"], 0.85 - y * 0.15 + (noise(x, y, 4, 50 + v) - 0.5) * 0.3, x, y);
      const r = (y - 4) % 7;
      const course = Math.floor((y - 4) / 7);
      if (r === 6) return BASALT[0];
      const ox = x + (course % 2) * 4 + v * 3;
      const c = ((ox % 8) + 8) % 8;
      if (c === 7) return BASALT[0];
      let k = 0.5 + (hash(Math.floor(ox / 8), course, 51 + v) - 0.5) * 0.3;
      if (r === 0 || c === 0) k += 0.2;
      else if (r === 5 || c === 6) k -= 0.18;
      return tone(BASALT, k, x, y);
    });
  } },
  { family: "brassworks", blurb: "Big pale dressed flags in staggered courses, a brass strip let into the top edge as a kerb.", paint: (ctx, v) => {
    const pale = ["#6e665c", "#8c8378", "#a69c90", "#bcb3a7", "#cfc7bc"] as const;
    pixels(ctx, S, S, (x, y) => {
      if (y < 3) return tone(BRASS, 0.9 - y * 0.25, x, y);
      const course = y < 17 ? 0 : 1;
      const r = y - (course ? 17 : 3);
      const h = course ? 15 : 14;
      if (r === h - 1) return "#3e3731";
      const ox = x + course * 8 + v * 5;
      const c = ((ox % 16) + 16) % 16;
      if (c === 15) return "#3e3731";
      let k = 0.5 + (hash(Math.floor(ox / 16), course, 53 + v) - 0.5) * 0.25 + (noise(x, y, 5, 54 + v) - 0.5) * 0.2;
      if (r === 0 || c === 0) k += 0.22;
      else if (r === h - 2 || c === 14) k -= 0.2;
      return tone(pale, k, x, y);
    });
  } },
  { family: "shipwright", blurb: "Round cobbles packed in sand, worn pale on top: a village lane.", paint: (ctx, v) => {
    const cobble = stoneField(["#4a4038", "#625750", "#7a6f66", "#8f857b", "#a39990"], 8, 54 + v * 53, { seam: 1.6, grit: 0.15, relief: 0.4 });
    pixels(ctx, S, S, (x, y) => {
      if (y < 4) return tone(["#8a8178", "#a69d94", "#b8b0a7", "#cac3ba"], 0.85 - y * 0.15 + (noise(x, y, 4, 55 + v) - 0.5) * 0.3, x, y);
      const c = cobble(x, y)!;
      return c === "#4a4038" ? "#6e6250" : c;
    });
  } },
];

const DECKING: BlockDesign[] = [
  { family: "ironclad", blurb: "Narrow tarred boards with an iron nosing strip along the top edge.", paint: (ctx, v) => {
    boards(ctx, TAR, 6, 60, v, false);
    for (let b = 0; b < 5; b++) {
      const j = 3 + Math.floor(hash(b, v, 61) * (S - 6));
      fill(ctx, IRON[2], j - 2, b * 6 + 2, 1, 1);
      fill(ctx, IRON[2], j + 2, b * 6 + 2, 1, 1);
    }
    fill(ctx, IRON[1], 0, 0, S, 4);
    fill(ctx, IRON[3], 0, 0, S, 1);
    fill(ctx, IRON[4], 0, 1, S, 1);
  } },
  { family: "brassworks", blurb: "Varnished deck boards with pale caulking lines and brass screw heads, a brass strip on the edge.", paint: (ctx, v) => {
    boards(ctx, MAHOGANY, 8, 62, v, false);
    for (let b = 1; b < 4; b++) fill(ctx, "#e8d8b0", 0, b * 8 - 1, S, 1);
    for (let b = 0; b < 4; b++) {
      const j = 4 + Math.floor(hash(b, v, 63) * (S - 8));
      brassBolt(ctx, j - 4, b * 8 + 3);
      brassBolt(ctx, j + 2, b * 8 + 3);
    }
    fill(ctx, BRASS[1], 0, 0, S, 3);
    fill(ctx, BRASS[3], 0, 0, S, 1);
  } },
  { family: "shipwright", blurb: "Broad oak boards pegged down, the top edge worn pale and rounded.", paint: (ctx, v) => {
    boards(ctx, WOOD, 16, 64, v, false);
    for (let b = 0; b < 2; b++) {
      const j = 4 + Math.floor(hash(b, v, 65) * (S - 8));
      disc(ctx, WOOD[0], j - 3.5, b * 16 + 7.5, 2);
      disc(ctx, WOOD[3], j - 3.5, b * 16 + 7.5, 1.2);
      disc(ctx, WOOD[0], j + 4.5, b * 16 + 7.5, 2);
      disc(ctx, WOOD[3], j + 4.5, b * 16 + 7.5, 1.2);
    }
    pixels(ctx, S, 4, (x, y) => tone(WOOD, 0.95 - y * 0.12 + (noise(x, y, 4, 66 + v) - 0.5) * 0.2, x, y), 0, 0);
  } },
];

const DECK_PLATE: BlockDesign[] = [
  { family: "ironclad", blurb: "Heavy tread plate: long raised bars laid in a herringbone, each lit on top and dark below, a bright worn rim, dark bluish iron.", paint: (ctx, v) => {
    pixels(ctx, S, S, (x, y) => {
      if (y < 4) return tone(ARMOUR, 0.95 - y * 0.12, x, y);
      if (y === S - 1) return ARMOUR[0];
      const row = Math.floor((y - 4) / 7);
      const gy = (y - 4) % 7;
      const gx = ((x + v * 5 + row * 4) % 10 + 10) % 10;
      // A bar runs diagonally across its cell, one way on even rows and the other on odd.
      const d = row % 2 ? gx - gy : gx + gy - 6;
      if (gy >= 1 && gy <= 5 && d >= 0 && d < 3) return d === 0 ? ARMOUR[4] : d === 1 ? ARMOUR[3] : ARMOUR[1];
      return tone(ARMOUR, 0.42 + (noise(x, y, 8, 70 + v) - 0.5) * 0.15, x, y, 0.4);
    });
    for (const [x, y] of [[2, 7], [28, 7], [2, 27], [28, 27]] as const) rivet(ctx, x, y);
  } },
  { family: "brassworks", blurb: "A bevelled iron plate with a brass rim along the top and six brass domes in two rows, the plate showing between.", paint: (ctx, v) => {
    pixels(ctx, S, S, (x, y) => {
      if (y < 4) return tone(BRASS, 0.9 - y * 0.15, x, y);
      if (x === S - 1 || y === S - 1) return IRON[0];
      if (x === 0 || y === 4) return IRON[4];
      return tone(IRON, 0.5 - ((x + y) / 64) * 0.2 + (noise(x, y, 8, 72 + v) - 0.5) * 0.12, x, y, 0.5);
    });
    for (const y of [12, 24]) for (const x of [6, 16, 26]) {
      const dx = (v % 2) * (y === 12 ? 1 : -1);
      disc(ctx, BRASS[0], x + dx, y, 3);
      disc(ctx, BRASS[3], x + dx - 0.4, y - 0.4, 2);
      fill(ctx, BRASS[4], x + dx - 1, y - 1, 1, 1);
    }
  } },
  { family: "shipwright", blurb: "Iron strips bolted over timber boards: the plate where feet fall, the wood between.", paint: (ctx, v) => {
    boards(ctx, WOOD, 8, 74, v, false);
    for (const y of [0, 14, 26]) {
      bar(ctx, 0, y, S, 6, IRON);
      for (const x of [3, 15, 27]) {
        fill(ctx, IRON[0], x, y + 2, 2, 2);
        fill(ctx, IRON[4], x, y + 2, 1, 1);
      }
    }
  } },
];

const BRACE: BlockDesign[] = [
  { family: "ironclad", blurb: "An iron angle frame with riveted gusset plates in the corners and a diagonal strut.", paint: (ctx) => {
    bar(ctx, 0, 0, 6, S, IRON);
    bar(ctx, 26, 0, 6, S, IRON);
    bar(ctx, 0, 0, S, 6, IRON);
    line(ctx, IRON[0], 5, 30, 28, 6, 5);
    line(ctx, IRON[2], 5, 29, 27, 6, 3);
    line(ctx, IRON[4], 4, 28, 26, 5, 1);
    for (const [x, y] of [[0, 0], [24, 0], [0, 24]] as const) {
      plate(ctx, x, y, 8, 8);
      rivet(ctx, x + 3, y + 3);
    }
  } },
  { family: "brassworks", blurb: "A mahogany frame with brass caps on the joints and a brass tie rod across.", paint: (ctx) => {
    beam(ctx, 0, 0, 6, S, MAHOGANY);
    beam(ctx, 26, 0, 6, S, MAHOGANY);
    beam(ctx, 0, 0, S, 6, MAHOGANY);
    line(ctx, BRASS[0], 5, 30, 28, 6, 4);
    line(ctx, BRASS[3], 5, 29, 27, 6, 2);
    for (const [x, y] of [[0, 0], [24, 0]] as const) {
      panel(ctx, x, y, 8, 8, BRASS[2], BRASS[4], BRASS[0]);
      brassBolt(ctx, x + 3, y + 3);
    }
  } },
  { family: "shipwright", blurb: "Timber frame lashed at the joints with rope, a timber strut pegged across.", paint: (ctx) => {
    beam(ctx, 0, 0, 6, S);
    beam(ctx, 26, 0, 6, S);
    beam(ctx, 0, 0, S, 6);
    line(ctx, WOOD[0], 5, 30, 28, 6, 5);
    line(ctx, WOOD[2], 5, 29, 27, 6, 3);
    line(ctx, WOOD[4], 4, 28, 26, 5, 1);
    for (const [x, y] of [[0, 0], [24, 0]] as const) pixels(ctx, 8, 8, (px, py) => ((px + py) % 3 === 0 ? ROPE[0] : (px + py) % 3 === 1 ? ROPE[2] : ROPE[1]), x, y);
  } },
];

const BRICK: BlockDesign[] = [
  { family: "ironclad", blurb: "New. Fired clinker brick, nearly black, in black mortar: fire-proof lining for engine rooms. Would be made from clay and coal at the workbench; a hull block that takes shapes and does not burn.", paint: (ctx, v) => bricks(ctx, BRICK_DARK, MORTAR[0], 16, 8, 80, v) },
  { family: "brassworks", blurb: "New. Red brick in pale lime mortar, English bond. Warm, tidy, a town house in the sky.", paint: (ctx, v) => bricks(ctx, BRICK_RED, MORTAR[2], 16, 8, 82, v) },
  { family: "shipwright", blurb: "New. Rough pale brick, uneven, in grey mortar: farmhouse brick, fired in a pit.", paint: (ctx, v) => {
    bricks(ctx, BRICK_PALE, MORTAR[1], 16, 8, 84, v);
    pixels(ctx, S, S, (x, y) => (hash(x, y, 85 + v) < 0.03 ? BRICK_PALE[1] : null));
  } },
];

const STONE_BLOCK: BlockDesign[] = [
  { family: "ironclad", blurb: "New. Great blocks of dark basalt ashlar with a chiselled margin: fortress walls. Cut from rock at the workbench; a hull block that takes shapes, tougher than timber.", paint: (ctx, v) => ashlar(ctx, BASALT, 90, v) },
  { family: "brassworks", blurb: "New. Dressed sandstone ashlar, warm and even, with a drafted margin round each block.", paint: (ctx, v) => ashlar(ctx, SANDSTONE, 92, v) },
  { family: "shipwright", blurb: "New. Fieldstone rubble, odd sizes packed in mortar: a drystone wall mortared up.", paint: (ctx, v) => {
    const stones = stoneField(FIELDSTONE, 11, 94 + v * 53, { seam: 1.8, grit: 0.2, relief: 0.35 });
    pixels(ctx, S, S, (x, y) => {
      const c = stones(x, y)!;
      return c === FIELDSTONE[0] ? MORTAR[1] : c;
    });
  } },
];

const LATTICE: BlockDesign[] = [
  { family: "ironclad", blurb: "New. An iron grid, see-through, in the background: cage walls, catwalk sides, a brig. Would be made from iron plate; non-solid, you walk in front of it.", paint: (ctx) => {
    lattice(ctx, 3, 8, IRON[0], IRON[4], true);
    pixels(ctx, S, S, (x, y) => (x % 8 === 1 || y % 8 === 1 ? IRON[2] : null));
  } },
  { family: "brassworks", blurb: "New. A diamond lattice of brass strips, a conservatory's trellis.", paint: (ctx) => {
    pixels(ctx, S, S, (x, y) => {
      const a = ((x + y) % 10 + 10) % 10;
      const b = ((x - y) % 10 + 10) % 10;
      if (a < 3 || b < 3) return a === 0 || b === 0 ? BRASS[4] : a === 2 || b === 2 ? BRASS[0] : BRASS[2];
      return null;
    });
  } },
  { family: "shipwright", blurb: "New. A timber trellis, thin laths crossed on the diagonal and nailed, for vines to climb.", paint: (ctx) => {
    pixels(ctx, S, S, (x, y) => {
      const a = ((x + y) % 10 + 10) % 10;
      const b = ((x - y) % 10 + 10) % 10;
      if (a < 3) return a === 0 ? WOOD[4] : a === 2 ? WOOD[0] : WOOD[2];
      if (b < 3) return b === 0 ? WOOD[3] : b === 2 ? WOOD[0] : WOOD[1];
      return null;
    });
    for (let y = 0; y < S; y += 10) for (let x = 0; x < S; x += 10) nail(ctx, x + 1, y + 1);
  } },
];

const RAILING: BlockDesign[] = [
  { family: "ironclad", blurb: "New. An iron railing: a flat top rail on square bars, ball finials on the posts. Stands in the background along a deck edge; non-solid. Would be made from iron plate.", paint: (ctx) => {
    for (const x of [6, 14, 22]) bar(ctx, x, 8, 2, S - 8, IRON);
    bar(ctx, 0, 4, S, 4, IRON);
    for (const x of [1, 29]) {
      bar(ctx, x, 6, 3, S - 6, IRON);
      disc(ctx, IRON[1], x + 1.5, 3, 2.5);
      disc(ctx, IRON[4], x + 1, 2.5, 1.2);
    }
  } },
  { family: "brassworks", blurb: "New. A brass hand rail on turned balusters, each swelling in the middle.", paint: (ctx) => {
    for (const x of [7, 16, 25]) {
      bar(ctx, x, 9, 2, S - 9, BRASS);
      disc(ctx, BRASS[1], x + 1, 20, 2.5);
      disc(ctx, BRASS[3], x + 0.5, 19.5, 1.5);
      fill(ctx, BRASS[3], x - 1, 9, 4, 2);
    }
    bar(ctx, 0, 4, S, 5, BRASS);
    fill(ctx, BRASS[4], 0, 5, S, 1);
  } },
  { family: "shipwright", blurb: "New. Rope strung between timber posts, sagging a little: the rail of a working deck.", paint: (ctx) => {
    beam(ctx, 2, 2, 5, S - 2);
    beam(ctx, 25, 2, 5, S - 2);
    ropeAcross(ctx, 6, 26, 8, 2.5);
    ropeAcross(ctx, 6, 26, 19, 2.5);
    nail(ctx, 4, 0);
    nail(ctx, 27, 0);
  } },
];

const GRATING: BlockDesign[] = [
  { family: "ironclad", blurb: "New. Heavy iron bar grating over dark: a deck you can see through, a drain, a hatch cover. A solid hull block that light and water pass. Would be made from iron plate.", paint: (ctx) => {
    fill(ctx, DARK);
    for (let y = 1; y < S; y += 6) bar(ctx, 0, y, S, 3, IRON);
    bar(ctx, 0, 0, 3, S, IRON);
    bar(ctx, 29, 0, 3, S, IRON);
    bar(ctx, 15, 0, 2, S, IRON);
  } },
  { family: "brassworks", blurb: "New. A brass grille of linked rings in a brass frame: an ornamental vent or floor grate.", paint: (ctx) => {
    fill(ctx, DARK);
    for (let gy = 0; gy < 2; gy++) for (let gx = 0; gx < 2; gx++) {
      const cx = 8 + gx * 16;
      const cy = 8 + gy * 16;
      disc(ctx, BRASS[0], cx, cy, 7);
      disc(ctx, DARK, cx, cy, 4.5);
      disc(ctx, BRASS[3], cx, cy, 6);
      disc(ctx, DARK, cx, cy, 4);
      fill(ctx, BRASS[4], cx - 4, cy - 5, 2, 1);
    }
    for (const [x, y, w, h] of [[0, 0, S, 3], [0, 0, 3, S], [0, S - 3, S, 3], [S - 3, 0, 3, S]] as const) panel(ctx, x, y, w, h, BRASS[2], BRASS[4], BRASS[0]);
  } },
  { family: "shipwright", blurb: "New. Timber slats with gaps between, two battens across: a duckboard grate.", paint: (ctx) => {
    fill(ctx, DARK);
    for (let x = 1; x < S; x += 6) beam(ctx, x, 0, 4, S);
    beam(ctx, 0, 4, S, 4);
    beam(ctx, 0, 24, S, 4);
    for (let x = 2; x < S; x += 6) {
      nail(ctx, x, 5);
      nail(ctx, x, 25);
    }
  } },
];

export const BUILDING_DESIGNS: BlockGroup[] = [
  { id: "hull-timber", title: "Timber hull", note: "The basic hull block; takes wedges and slopes. Burns.", key: "PLANK", designs: HULL_TIMBER },
  { id: "hull-iron", title: "Iron plate", note: "Armoured hull, three hits; takes shapes.", key: "IRON", designs: HULL_IRON },
  { id: "hull-glass", title: "Glass", note: "A window block; takes shapes. Made from void crystal or sun-glass.", key: "GLASS", designs: HULL_GLASS },
  { id: "back-wall", title: "Back wall", note: "The inside of a hull, in the background. Seals a ship's interior.", key: "WALL", designs: BACK_WALL },
  { id: "ladder", title: "Ladder", note: "Climbed; drawn over the back wall.", key: "LADDER", designs: LADDER },
  { id: "paving", title: "Paving", note: "Laid ground: smooths to the ground round it; the top band is its worn surface.", key: "PAVING", designs: PAVING },
  { id: "decking", title: "Decking", note: "Laid timber ground, following the slope.", key: "DECKING", designs: DECKING },
  { id: "deck-plate", title: "Deck plate", note: "Laid iron ground, three hits.", key: "DECK_PLATE", designs: DECK_PLATE },
  { id: "brace", title: "Brace", note: "A frame in the background: living bark won't heal over near one.", key: "BRACE", designs: BRACE },
  { id: "brick", title: "Brick", key: "BRICK", note: "Fired at the workbench from clay and coal: a hull block that takes shapes and does not burn, between timber and iron in toughness.", designs: BRICK },
  { id: "stone-block", title: "Stone block", key: "STONE", note: "Rock cut square at the workbench: a hull block that takes shapes, heavier and tougher than timber, cheap where rock is. Homes in rock and keeps on ships.", designs: STONE_BLOCK },
  { id: "lattice", title: "Lattice", key: "LATTICE", note: "See-through and in the background like a back wall, but it does not seal a ship: cages, catwalk sides, trellis for vines.", designs: LATTICE },
  { id: "railing", title: "Railing", key: "RAILING", note: "One tile tall in the background along a deck edge; you walk in front of it. Decoration with a job: it shows where the deck ends.", designs: RAILING },
  { id: "grating", title: "Grating", key: "GRATING", note: "A solid hull block you can see through, like glass but open: drains, deck gratings, hatch covers. Light passes; so would water.", designs: GRATING },
];

/** Painted over its own family's back wall (the rest of the background kinds are see-through). */
const OVER_WALL = new Set(["LADDER"]);

/** Every building block's painter, by its name in the registry (IRONCLAD_PLANK ...). */
export const BUILD_PAINT: Readonly<Record<string, (ctx: Ctx, v: number) => void>> = Object.fromEntries(
  BUILDING_DESIGNS.flatMap((g) =>
    g.designs.map((d) => {
      const wall = BACK_WALL.find((w) => w.family === d.family)!;
      const paint: (ctx: Ctx, v: number) => void = OVER_WALL.has(g.key) ? (ctx, v) => (wall.paint(ctx, v), d.paint(ctx, v)) : d.paint;
      return [`${d.family.toUpperCase()}_${g.key}`, paint];
    }),
  ),
);
